import React, { createContext, useMemo, useReducer } from 'react';

import {
  APP_CURRENT_PROJECT_ID_LS_KEY, APP_CURRENT_PROJECT_NAME_LS_KEY, APP_CURRENT_PROJECT_ROLE_LS_KEY, ProjectRole,
} from '../constants';

const appCurrentProjectId = window.localStorage.getItem(APP_CURRENT_PROJECT_ID_LS_KEY);
const appCurrentProjectName = window.localStorage.getItem(APP_CURRENT_PROJECT_NAME_LS_KEY);
const appCurrentProjectRole = window.localStorage.getItem(APP_CURRENT_PROJECT_ROLE_LS_KEY);

interface AppState {
  currentProjectId: string | null;
  currentProjectName: string | null;
  currentProjectRole: ProjectRole | null;
  currentProjectLastUpdate: number | null;
  acceptedProjectInvitationId: string | null;
}

const initialState: AppState = {
  currentProjectId: appCurrentProjectId,
  currentProjectName: appCurrentProjectName,
  currentProjectRole: appCurrentProjectRole as ProjectRole,
  currentProjectLastUpdate: null,
  acceptedProjectInvitationId: null,
};

export enum AppActionType {
  RESET_STATE = 'RESET_STATE',
  CHANGE_CURRENT_PROJECT = 'CHANGE_CURRENT_PROJECT',
  UPDATE_CURRENT_PROJECT = 'UPDATE_CURRENT_PROJECT',
  ACCEPT_PROJECT_INVITATION = 'ACCEPT_PROJECT_INVITATION',
}

interface AppAction {
  type: AppActionType
}

interface ResetStateAction extends AppAction { }

interface ChangeCurrentProjectAction extends AppAction {
  payload: {
    projectId: string
    name: string
    role: ProjectRole
  }
}

interface UpdateCurrentProjectAction extends AppAction {
  payload: {
    name: string
    role: ProjectRole
  }
}

interface AcceptProjectInvitationAction extends AppAction {
  payload: {
    projectId: string
  }
}

export type AppActions = ResetStateAction | ChangeCurrentProjectAction
  | UpdateCurrentProjectAction | AcceptProjectInvitationAction;

const appReducer = (state: AppState, action: AppActions): AppState => {
  const { type } = action;
  switch (type) {
    case AppActionType.RESET_STATE: {
      window.localStorage.removeItem(APP_CURRENT_PROJECT_ID_LS_KEY);
      window.localStorage.removeItem(APP_CURRENT_PROJECT_NAME_LS_KEY);
      window.localStorage.removeItem(APP_CURRENT_PROJECT_ROLE_LS_KEY);
      return {
        ...state,
        currentProjectId: null,
        currentProjectName: null,
        currentProjectRole: null,
      };
    }
    case AppActionType.CHANGE_CURRENT_PROJECT: {
      const { payload } = action as ChangeCurrentProjectAction;
      window.localStorage.setItem(APP_CURRENT_PROJECT_ID_LS_KEY, payload.projectId);
      window.localStorage.setItem(APP_CURRENT_PROJECT_NAME_LS_KEY, payload.name);
      window.localStorage.setItem(APP_CURRENT_PROJECT_ROLE_LS_KEY, payload.role);
      return {
        ...state,
        currentProjectId: payload.projectId,
        currentProjectName: payload.name,
        currentProjectRole: payload.role,
      };
    }
    case AppActionType.UPDATE_CURRENT_PROJECT: {
      const { payload } = action as UpdateCurrentProjectAction;
      window.localStorage.setItem(APP_CURRENT_PROJECT_NAME_LS_KEY, payload.name);
      window.localStorage.setItem(APP_CURRENT_PROJECT_ROLE_LS_KEY, payload.role);
      return {
        ...state,
        currentProjectLastUpdate: Date.now(),
        currentProjectName: payload.name,
        currentProjectRole: payload.role,
      };
    }
    case AppActionType.ACCEPT_PROJECT_INVITATION: {
      const { payload } = action as AcceptProjectInvitationAction;
      return {
        ...state,
        acceptedProjectInvitationId: payload.projectId,
      };
    }
    default:
      return state;
  }
};

export const AppContext = createContext({} as { appState: AppState, appDispatch: React.Dispatch<AppActions> });

export const AppProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const providerValue = useMemo(() => ({
    appState: state,
    appDispatch: dispatch,
  }), [state]);
  return (
    <AppContext.Provider value={providerValue}>
      {children}
    </AppContext.Provider>
  );
};
